import { useState } from "react";

import { BellAlertIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IInboxConversation, IOrderAction } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { ebayCancelReasons, ebayRefundReasons, IEbayReason } from "../../utils/ebayReasons";
import { orderActionBlock, orderTotal, refundAmountOk, remainingRefundable } from "../../utils/inboxOrderActions";
import { trackingLink } from "../../utils/trackingLink";
import PriceFormat from "../../utils/priceFormat";
import { IInboxNotice, InboxNotice, PreviewNote, useInboxLive } from "./InboxBits";

const signOff = "\n\nKind regards,\n{shop_name}";

/** Reasons straight from eBay's own lists (DASH/utils/ebayReasons.ts). */
function reasonsFor(c: IInboxConversation, kind: IOrderAction["kind"]): IEbayReason[] {
  // A refund through an eBay return goes via the return itself, which takes no reason code.
  if (kind !== "cancel" && c.returnCase) return [];
  if (kind === "cancel") return ebayCancelReasons(c.order?.paymentStatus === "Paid");
  return ebayRefundReasons(kind === "refund_full" ? "full" : "partial");
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function actionTitle(kind: IOrderAction["kind"], t: Translate) {
  if (kind === "cancel") return t("Cancel order");
  if (kind === "refund_full") return t("Refund in full");
  return t("Partial refund");
}

/** Which channel can do what today, from the code (plan §8). */
function channelSupport(c: IInboxConversation, kind: IOrderAction["kind"], t: Translate) {
  if (c.channel === "partpro") return t("Wilma can't do this on PartPro yet – it needs a PartPro API call.");
  if (kind === "cancel") return t("Uses Wilma's existing eBay cancel (only before dispatch).");
  if (c.returnCase) return t("Goes through the eBay return, which Wilma can already refund.");
  return t("Needs eBay's order refund call, which Wilma doesn't have yet.");
}

function defaultMessage(kind: IOrderAction["kind"], amount: number) {
  if (kind === "cancel") return `Hi {buyer_first_name}, we've cancelled your order as requested. You'll get a full refund from eBay shortly.${signOff}`;
  if (kind === "refund_full") return `Hi {buyer_first_name}, we've refunded your order in full (${PriceFormat(amount)}). Sorry for the trouble.${signOff}`;
  return `Hi {buyer_first_name}, sorry about that. We've sent you a partial refund of ${PriceFormat(amount)} – thanks for being understanding.${signOff}`;
}

/**
 * Cancel, refund or part-refund an order, always confirmed by a person, with the amount, a reason
 * and the message to the buyer in one place. On live data it really happens once confirmed; on
 * sample data nothing is cancelled or refunded.
 */
export function OrderActionPanel({
  c,
  kind,
  fillMerge,
  notice,
  onConfirm,
  onClose,
}: {
  c: IInboxConversation;
  kind: IOrderAction["kind"];
  /** Fills {buyer_first_name} etc., so the message reads as it will be sent. */
  fillMerge: (text: string) => string;
  /** Live data: what the last cancel or refund on this conversation came back with. Kept by the page, so it outlives the panel. */
  notice?: IInboxNotice;
  /** Live data: resolves with what happened (done, blocked or refused). */
  onConfirm: (action: IOrderAction) => Promise<IInboxNotice | undefined> | void;
  onClose: () => void;
}) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const order = c.order;
  const remaining = order ? remainingRefundable(order) : 0;
  const [partialAmount, setPartialAmount] = useState("");
  const reasons = reasonsFor(c, kind);
  const [reasonCode, setReasonCode] = useState(reasons[0]?.code);
  const [message, setMessage] = useState<string>();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  if (!order) return null;

  // A full refund is always what's left, as the order stands now (the context can refresh under us).
  const amount = kind === "refund_partial" ? partialAmount : remaining.toFixed(2);
  const value = Number(amount);
  const amountOk = kind === "cancel" || refundAmountOk(order, value);
  const block = orderActionBlock(order, kind);
  const text = message ?? fillMerge(defaultMessage(kind, amountOk ? value || remaining : 0));
  const reasonLabel = reasons.find((r) => r.code === reasonCode)?.label;
  const done = notice?.tone === "green";

  async function confirm() {
    if (!order || pending) return;
    setPending(true);
    try {
      await onConfirm({ kind, amount: kind === "cancel" ? undefined : value, reasonCode, reason: reasonLabel, messageToBuyer: text });
    } finally {
      setPending(false);
    }
  }

  const actionText =
    kind === "cancel"
      ? t("Cancel order {{number}}", { number: order.orderNumber })
      : t("Refund {{amount}} on order {{number}}", { amount: PriceFormat(value), number: order.orderNumber });

  return (
    <section className="rounded-xl border-2 border-primary-300 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Order {{number}}", { number: order.orderNumber })}</p>
          <p className="text-[16px] font-semibold text-gray-900">{actionTitle(kind, t)}</p>
        </div>
        <button type="button" onClick={onClose} disabled={pending} className="text-[13px] text-gray-500 hover:text-gray-800 disabled:opacity-50">
          {t("Close")}
        </button>
      </div>

      {done ? (
        <>
          <InboxNotice notice={notice} className="mt-2" />
          {text.trim() && <p className="mt-2 text-[12px] text-gray-500">{t("The message to the buyer is in the reply box – check it and press Send.")}</p>}
        </>
      ) : block ? (
        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">{block}</p>
      ) : (
        <>
          {kind !== "cancel" && (
            <label className="mt-3 block text-[12px] text-gray-600">
              {t("Amount")} {kind === "refund_partial" && <span className="text-gray-400">{t("(up to {{total}})", { total: PriceFormat(remaining) })}</span>}
              <div className="mt-1 flex items-center gap-1">
                <span className="text-[14px] text-gray-500">£</span>
                <input
                  value={amount}
                  onChange={(e) => {
                    setPartialAmount(e.target.value);
                    setMessage(undefined);
                    setConfirming(false);
                  }}
                  readOnly={kind === "refund_full" || confirming}
                  inputMode="decimal"
                  className={classNames("w-28 rounded-lg border px-2 py-1.5 text-[14px]", amount && !amountOk ? "border-red-400" : "border-gray-300", (kind === "refund_full" || confirming) && "bg-gray-50")}
                />
                {kind === "refund_full" && (
                  <span className="text-[12px] text-gray-500">
                    {order.refundedSoFar
                      ? t("{{total}} paid, {{refunded}} already refunded", { total: PriceFormat(orderTotal(order)), refunded: PriceFormat(order.refundedSoFar) })
                      : t("item {{price}} + postage {{postage}}", { price: PriceFormat(order.pricePaid), postage: PriceFormat(order.postage) })}
                  </span>
                )}
              </div>
            </label>
          )}
          {reasons.length > 0 ? (
            <label className="mt-3 block text-[12px] text-gray-600">
              {t("Reason")} <span className="text-gray-400">{c.channel === "partpro" ? t("(eBay's list – PartPro's to be confirmed)") : t("(eBay's list)")}</span>
              <select value={reasonCode} disabled={confirming} onChange={(e) => setReasonCode(e.target.value)} className="mt-1 block w-full rounded-lg border-gray-300 py-1.5 text-[13px]">
                {reasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="mt-3 text-[12px] text-gray-500">{t("Refunded through the eBay return, which doesn't take a reason.")}</p>
          )}
          <label className="mt-3 block text-[12px] text-gray-600">
            {t("Message to the buyer")}
            <textarea value={text} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1 block w-full rounded-lg border-gray-300 text-[13px]" />
          </label>
          {live ? (
            <p className="mt-2 text-[12px] text-gray-500">{t("Once it's done, this message goes in the reply box for you to check and send.")}</p>
          ) : (
            <p className="mt-2 text-[12px] text-gray-500">{channelSupport(c, kind, t)}</p>
          )}

          {confirming ? (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900">
              <p className="font-semibold">{actionText}?</p>
              {reasonLabel && <p className="mt-0.5">{t("Reason: {{reason}}", { reason: reasonLabel })}</p>}
              <p className="mt-0.5">{live ? t("This goes to {{channel}} straight away and can't be undone.", { channel: c.channel === "ebay" ? "eBay" : "PartPro" }) : t("Preview: nothing is cancelled or refunded, and the message isn't sent.")}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={pending}
                  onClick={confirm}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {pending ? t("Working…") : kind === "cancel" ? t("Yes, cancel the order") : t("Yes, refund {{amount}}", { amount: PriceFormat(value) })}
                </button>
                <button type="button" disabled={pending} onClick={() => setConfirming(false)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-700 hover:bg-white disabled:opacity-50">
                  {t("Back")}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={!amountOk}
              onClick={() => setConfirming(true)}
              className="mt-3 w-full rounded-lg bg-primary-600 py-2 text-[14px] font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {kind === "cancel" ? t("Cancel order") : amountOk ? t("Refund {{amount}}", { amount: PriceFormat(value) }) : t("Refund")}
            </button>
          )}
          {notice && <InboxNotice notice={notice} className="mt-2" />}
          {!live && <PreviewNote>{t("Preview: nothing is cancelled or refunded, and the message isn't sent.")}</PreviewNote>}
        </>
      )}
    </section>
  );
}

/**
 * Urgent message to the whole warehouse, not one person: posted to a #warehouse Team Chat channel,
 * which pushes to the Wilma app on their phones. Preview: nothing is sent.
 */
export function WarehouseAlertPanel({ text, onSend, onClose }: { text: string; onSend: (text: string) => void; onClose: () => void }) {
  const { t } = useTranslation("messages");
  const [body, setBody] = useState(text);
  return (
    <div className="mt-3 rounded-lg border border-gray-300 bg-white p-3 text-gray-900">
      <p className="flex items-center gap-1.5 text-[13px] font-semibold">
        <BellAlertIcon className="h-4 w-4 text-red-600" /> {t("Urgent alert to #warehouse")}
      </p>
      <p className="text-[12px] text-gray-500">{t("Everyone in the warehouse channel gets a push notification on their phone.")}</p>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="mt-2 block w-full rounded-lg border-gray-300 text-[13px]" />
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={() => onSend(body)} className="rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-red-700">
          {t("Send alert")}
        </button>
        <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100">
          {t("Cancel")}
        </button>
      </div>
      <PreviewNote>{t("Preview: the alert isn't sent. It needs a #warehouse channel and Wilma posting to Team Chat itself (plan §8).")}</PreviewNote>
    </div>
  );
}


/** An eBay case on the order, with what eBay expects and the actions that settle it. */
export function EbayCaseCard({ c, onAction, onSendAnswer }: { c: IInboxConversation; onAction: (kind: IOrderAction["kind"]) => void; onSendAnswer: (text: string) => void }) {
  const { t } = useTranslation("messages");
  const k = c.ebayCase;
  if (!k) return null;
  const caseTitles = { item_not_received: t("Item not received"), not_as_described: t("Not as described") };
  const caseStages = { awaiting_seller: t("Waiting for you"), awaiting_buyer: t("Waiting for the buyer"), escalated: t("eBay has stepped in – they'll decide if there's no agreement") };
  const o = c.order;
  const left = DateTime.fromISO(k.respondBy, { zone: "utc" }).diff(DateTime.utc(), ["days", "hours"]);
  const leftText = left.toMillis() > 0 ? t("{{days}}d {{hours}}h", { days: Math.floor(left.days), hours: Math.floor(left.hours) }) : t("Overdue");

  const advice =
    k.kind === "item_not_received"
      ? o?.trackingNumber
        ? t("It's tracked, so share the tracking and the latest courier update. If it's lost, refund in full.")
        : t("No tracking on this order – eBay will usually side with the buyer. Refund in full unless it can be found.")
      : t("Settle it with the buyer before eBay decides: a partial refund if they're keeping it, or accept a return.");

  const trackingReply =
    o && `Hi {buyer_first_name}, sorry it hasn't arrived yet. It went out with ${o.courier ?? "the courier"}` +
    (o.trackingNumber ? ` – tracking ${trackingLink(o) ?? o.trackingNumber}` : "") +
    `. We've asked them for an update and will come back to you as soon as we hear.\n\nKind regards,\n{shop_name}`;

  return (
    <section className="overflow-hidden rounded-xl border border-red-300 bg-white">
      <div className="bg-red-50 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-red-800">{t("eBay case")} · {caseTitles[k.kind]}</p>
        <p className="text-[16px] font-semibold text-gray-900">{k.reason}</p>
        <p className="text-[13px] text-red-800">{caseStages[k.stage]}</p>
      </div>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="text-[12px] text-gray-500">
          {t("Case {{id}}", { id: k.caseId })}
          <br />
          {t("Buyer wants:")} <span className="font-medium text-gray-900">{k.buyerWants === "replacement" ? t("a replacement") : t("a refund")}</span>
        </div>
        <div className="text-right">
          <p className="text-[12px] text-gray-500">{t("Respond by")}</p>
          <p className={classNames("text-[22px] font-bold", left.as("hours") < 24 ? "text-red-700" : "text-amber-700")}>{leftText}</p>
        </div>
      </div>
      <p className="border-t border-gray-100 px-4 py-3 text-[13px] text-gray-700">{advice}</p>
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        {k.kind === "item_not_received" && trackingReply && (
          <button type="button" onClick={() => onSendAnswer(trackingReply)} className="rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700">
            {t("Draft tracking reply")}
          </button>
        )}
        {k.kind === "not_as_described" && (
          <button type="button" onClick={() => onAction("refund_partial")} className="rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700">
            {t("Partial refund…")}
          </button>
        )}
        <button type="button" onClick={() => onAction("refund_full")} className="rounded-lg border border-gray-300 px-3 py-1.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50">
          {t("Refund in full…")}
        </button>
        {k.kind === "not_as_described" && (
          <button type="button" disabled title={t("Comes with the returns work")} className="rounded-lg border border-gray-300 px-3 py-1.5 text-[13px] text-gray-400">
            {t("Accept return")}
          </button>
        )}
      </div>
      <PreviewNote>
        <span className="px-4">{t("Wilma doesn't read eBay cases yet – this needs eBay's inquiry and case APIs (plan §8).")}</span>
      </PreviewNote>
      <div className="h-3" />
    </section>
  );
}
